import { useTableColumnsContext, TableColumn, getMaxDepth } from "@components/Table/TableColumns";
import TableCell from "@components/Table/TableCell";
import { FCX, getScaledPixels, useWindowSizeDebounced } from "@datassential/platform-ui-lib";
import cn from "classnames";
import React, { useMemo } from "react";

const getColumnDepth = (col: TableColumn): number => {
    if (!col.subColumns?.length) return 1;
    return 1 + Math.max(...col.subColumns.map(getColumnDepth));
};

const TableHead: FCX<{
    height: number;
    isWindowsScroller?: boolean;
}> = ({
    height,
    isWindowsScroller = false,
    style,
}) => {
    const [wWidth] = useWindowSizeDebounced();
    const { data } = useTableColumnsContext();

    const maxDepth = useMemo(() => getMaxDepth(data), [data]);
    const totalHeight = useMemo(() => getScaledPixels(height, wWidth), [height, wWidth]);
    const perLevelHeight = useMemo(
        () => (maxDepth > 0 ? totalHeight / maxDepth : totalHeight),
        [maxDepth, totalHeight]
    );

    const renderColumn = (col: TableColumn, index: number, level: number = 0): React.ReactNode => {
        const hasSub = Array.isArray(col.subColumns) && col.subColumns.length > 0;
        const columnDepth = getColumnDepth(col);
        const remainingDepth = columnDepth - 1;

        return (
            <TableCell
                key={col.key ?? `col-${index}`}
                isHead
                isSub={level > 0}
                accentLevel={col.accentLevel}
                lockAccent={col.lockAccent}
                hasSub={hasSub}
                sortingKey={col.sortKey}
                style={{
                    ...col.style?.cell,
                    ...col.style?.head,
                }}
                isSortHighlightHidden={col.isSortHighlightHidden ?? (index === 0 && level === 0)}
                testId={`tableHeaderColumn${index}`}
                {...col.getHtmlAttributes?.(undefined)}
            >
                {hasSub ? (
                    <>
                        <div
                            className="TableCellColumns"
                            style={{
                                height: perLevelHeight,
                                flex: '0 0 auto',
                            }}
                        >
                            <TableCell
                                key={`${col.key}-head`}
                                isHead
                                isSub
                                accentLevel={col.accentLevel}
                                lockAccent={col.lockAccent}
                                style={{
                                    ...col.style?.cell,
                                    ...col.style?.head,
                                    flex: 1,
                                }}
                            >
                                {col.head}
                            </TableCell>
                        </div>

                        <div
                            className="TableCellColumns"
                            style={{
                                height: remainingDepth * perLevelHeight,
                                flex: '0 0 auto',
                            }}
                        >
                            {col.subColumns?.map((subCol, subIndex) =>
                                renderColumn(subCol, subIndex, level + 1)
                            )}
                        </div>
                    </>
                ) : (
                    col.head
                )}
            </TableCell>
        );
    };

    return (
        <div
            className={cn(
                "TableRow is-head",
                isWindowsScroller && "is-window-scroller",
            )}
            style={{
                ...style,
                height: totalHeight,
            }}
        >
            {data.map((col, index) => renderColumn(col, index, 0))}
        </div>
    );
};

export default TableHead;
